/**
 * Shared helpers for matching uploaded photo filenames to products,
 * used by both the bulk uploader and the image linker page.
 */

/**
 * Extracts a base image name like "img_4256" from "IMG_4256.jpg" or a
 * full R2/Cloudinary URL such as ".../IMG_4256_p4qaoy.jpg".
 */
export function extractBaseName(filenameOrUrl) {
  if (!filenameOrUrl) return "";
  const clean = filenameOrUrl.split("/").pop().split("?")[0];
  const match = clean.match(/(IMG_\d+|[a-zA-Z0-9_-]+?)(?:_[a-z0-9]+)?\.[a-z0-9]+$/i);
  return match ? match[1].toLowerCase() : clean.replace(/\.[^/.]+$/, "").toLowerCase();
}

/**
 * Pulls the trailing number out of a base name, e.g. "img_4256" -> 4256.
 * Returns null when the base name has no trailing digits (e.g. a UUID).
 */
export function extractNumericSuffix(baseName) {
  if (!baseName) return null;
  // UUID-style names (contain hyphens) end in digits too but aren't
  // sequential camera numbers — exclude them so they don't get sorted
  // into the middle of an unrelated numeric range.
  if (baseName.includes("-")) return null;
  const match = baseName.match(/(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Groups items (each needs a `.baseName`) into clusters by camera-roll
 * proximity — items whose numeric suffix is within `gapThreshold` of the
 * previous one land in the same cluster. Mirrors how a phone camera roll
 * looks when someone shoots one product, then moves to the next.
 *
 * Items with no numeric suffix (e.g. UUID-named uploads) go into their
 * own trailing "Other" cluster.
 *
 * @returns {Array<{ items: T[], rangeLabel: string }>}
 */
export function clusterByProximity(items, gapThreshold = 20) {
  const numbered = [];
  const unnumbered = [];

  for (const item of items) {
    const n = extractNumericSuffix(item.baseName);
    if (n === null) unnumbered.push(item);
    else numbered.push({ ...item, __n: n });
  }

  numbered.sort((a, b) => a.__n - b.__n);

  const clusters = [];
  let current = [];
  for (const item of numbered) {
    if (current.length === 0 || item.__n - current[current.length - 1].__n <= gapThreshold) {
      current.push(item);
    } else {
      clusters.push(current);
      current = [item];
    }
  }
  if (current.length > 0) clusters.push(current);

  const result = clusters.map((cluster) => ({
    items: cluster.map(({ __n, ...rest }) => rest),
    rangeLabel:
      cluster.length === 1
        ? cluster[0].baseName
        : `${cluster[0].baseName} – ${cluster[cluster.length - 1].baseName}`,
  }));

  if (unnumbered.length > 0) {
    result.push({ items: unnumbered, rangeLabel: "Other" });
  }

  return result;
}
